import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import RotateDevice from "../../components/RotateDevice/RotateDevice";
import { API_URL, authHeaders, formatData, FullscreenBtn, KpiCard, ico } from "./comuni";
import { AggiornaBtn, ViewToggle, useEscChiude, formatDataOra } from "./percorsiCondivisi";
import ModaleConvalidaBozza from "./ModaleConvalidaBozza";

/**
 * Sotto-tab "Pianificati" del tab Percorsi (task 517): SOLO le bozze (tabella locale
 * percorsi_bozze), generate alle 08:00 dai turni GECOS di domani (il venerdì anche del
 * lunedì). Qui si convalidano; alle 20:00 il job le pubblica su Interacta come post 122
 * "Da Fare". Il codice delle bozze è quello che prima stava in cima a TabPercorsi.js.
 * Legge dal DB locale, non da Interacta: è veloce.
 */

// Stati della bozza: etichetta e colore del badge.
// "bozza" = pianificato da GECOS, ancora da convalidare (ambra); "confermata" = pronto per le 20:00 (blu).
const BOZZA_STATO = {
    bozza: { label: "Pianificato", cls: "badge-warning" },
    confermata: { label: "Confermato", cls: "badge-info" },
    pubblicata: { label: "Pubblicata", cls: "badge-success" },
    non_generabile: { label: "Non generabile", cls: "badge-neutral" },
    errore: { label: "Errore", cls: "badge-danger" },
    scartata: { label: "Scartata", cls: "badge-neutral" },
};
const bozzaStato = (stato) => BOZZA_STATO[stato] || { label: stato || "—", cls: "badge-neutral" };

// Entro quando va convalidata una bozza ("sab 26/09, 20:00"): col giorno della settimana,
// perché il venerdì convivono le bozze del sabato (scadenza stasera) e del lunedì (domani sera).
// Per le bozze già chiuse (pubblicata/scartata) la scadenza non conta più.
const formatScadenza = (b) => {
    if (b.stato === "pubblicata" || b.stato === "scartata" || !b.scadenza_convalida) return "—";
    const d = new Date(b.scadenza_convalida);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

// Bozze raggruppate per giorno del giro, per separare a colpo d'occhio sabato e lunedì (il
// venerdì escono insieme). La lista arriva già ordinata per data dal backend: basta spezzarla
// quando la data cambia. Etichetta tipo "Sabato 26/09"; T12:00 evita slittamenti di fuso.
const gruppiPerData = (bozze) => {
    const gruppi = [];
    for (const b of bozze) {
        const ultimo = gruppi[gruppi.length - 1];
        if (ultimo && ultimo.data === b.data) ultimo.bozze.push(b);
        else gruppi.push({ data: b.data, bozze: [b] });
    }
    return gruppi.map((g) => {
        const d = new Date(`${g.data}T12:00:00`);
        const etichetta = Number.isNaN(d.getTime()) ? g.data
            : d.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "2-digit" });
        return { ...g, etichetta: etichetta.charAt(0).toUpperCase() + etichetta.slice(1) };
    });
};
const etichettaGruppo = (g) => `${g.etichetta} · ${g.bozze.length} ${g.bozze.length === 1 ? "percorso" : "percorsi"}`;

const VUOTO = "Nessuna bozza in attesa. Le bozze di domani (il venerdì anche quelle del lunedì) vengono generate alle 08:00 dai turni GECOS.";

const SezionePianificati = ({ zona, viewMode, onViewMode }) => {
    const [bozze, setBozze] = useState([]);
    const [bozzeInfo, setBozzeInfo] = useState({ run: {}, oraPubblicazione: "20:00", prossimeDate: [] });
    const [bozzeLoading, setBozzeLoading] = useState(false);
    const [fsBozze, setFsBozze] = useState(false);
    const [bozzaAperta, setBozzaAperta] = useState(null); // bozza nel modale di convalida

    const fetchBozze = useCallback(async () => {
        setBozzeLoading(true);
        try {
            const res = await axios.get(`${API_URL}/gestione-percorsi/bozze`, { headers: authHeaders(), params: { zona } });
            setBozze(res.data?.data ?? []);
            setBozzeInfo({ run: res.data?.run || {}, oraPubblicazione: res.data?.oraPubblicazione || "20:00", prossimeDate: res.data?.prossimeDate || [] });
        } catch (err) {
            console.error("Errore nel fetch bozze percorsi:", err);
            setBozze([]);
        } finally {
            setBozzeLoading(false);
        }
    }, [zona]);

    useEffect(() => { fetchBozze(); }, [fetchBozze]);

    const chiudiFs = useCallback(() => setFsBozze(false), []);
    useEscChiude(fsBozze, chiudiFs);

    // Operatori Interacta (stesso endpoint del modale "Genera"): servono per i nomi degli
    // assegnatari in tabella e per la select del modale. Caricati una volta, e solo se
    // ci sono bozze (sono ~800 utenti: inutile scaricarli quando la sezione è vuota).
    const [utentiList, setUtentiList] = useState(null);
    const [utentiLoading, setUtentiLoading] = useState(false);
    const utentiRichiesti = useRef(false); // guardia: una sola richiesta per montaggio
    useEffect(() => {
        if (utentiRichiesti.current || bozze.length === 0) return;
        utentiRichiesti.current = true;
        setUtentiLoading(true);
        axios.get(`${API_URL}/gestione-percorsi/utenti`, { headers: authHeaders() })
            .then((res) => setUtentiList(res.data?.data ?? []))
            .catch((err) => { console.error("Errore caricamento utenti:", err); setUtentiList([]); })
            .finally(() => setUtentiLoading(false));
    }, [bozze.length]);
    const utentiOptions = useMemo(() => (utentiList || []).map((u) => ({ value: u.id, label: u.nome })), [utentiList]);
    const nomeUtente = useMemo(() => {
        const m = new Map((utentiList || []).map((u) => [Number(u.id), u.nome]));
        return (id) => m.get(Number(id)) || `#${id}`;
    }, [utentiList]);

    // KPI calcolati dalla lista (sono poche righe, il backend non serve).
    const conta = (stato) => bozze.filter((b) => b.stato === stato).length;
    const bozzeAperte = conta("bozza");
    const titoloBozze = (
        <>Bozze da convalidare <span className="gp-section-count">{bozzeLoading && bozze.length === 0 ? "…" : bozzeAperte}</span></>
    );

    // Riga di stato: ultimo run "genera" (con avviso se fallito: il job riprova da solo ogni
    // 30 min) e promemoria della pubblicazione automatica.
    const runGenera = bozzeInfo.run?.genera;
    const runPubblica = bozzeInfo.run?.pubblica;
    const statusBozze = (
        <div className={`gp-bozze-status${runGenera?.esito === "errore" ? " is-errore" : ""}`}>
            {runGenera?.esito === "errore" ? (
                <span>⚠ Ultima generazione fallita alle {formatDataOra(runGenera.avviato_il)}: il sistema riprova automaticamente ogni 30 minuti.</span>
            ) : runGenera ? (
                <span>Bozze generate il {formatDataOra(runGenera.avviato_il)} dai turni GECOS.</span>
            ) : (
                <span>Nessuna generazione ancora eseguita.</span>
            )}
            <span>Pubblicazione automatica su Interacta alle <strong>{bozzeInfo.oraPubblicazione}</strong>{runPubblica ? ` · ultima: ${formatDataOra(runPubblica.avviato_il)} (${runPubblica.contatori?.pubblicate ?? 0} pubblicate)` : ""}.</span>
        </div>
    );

    // Azioni di una bozza: si può solo convalidare (bozza) o modificare (confermata/errore).
    // Niente "scarta": le non convalidate le scarta il job delle 20:00.
    const azioniBozza = (b) => {
        if (b.stato === "bozza") {
            return <button className="gp-dettaglio-btn" onClick={() => setBozzaAperta(b)} title="Scegli gli assegnatari e conferma">Convalida</button>;
        }
        if (b.stato === "confermata" || b.stato === "errore") {
            return <button className="gp-genera-btn" onClick={() => setBozzaAperta(b)} title="Modifica assegnatari o ordine delle tratte">Modifica</button>;
        }
        return <span className="muted">—</span>;
    };
    const bozzaSalvata = () => { setBozzaAperta(null); fetchBozze(); };

    return (
        <>
            <div className="gp-toolbar-row">
                <div className="toolbar">
                    <div className="spacer" />
                    <AggiornaBtn onClick={fetchBozze} />
                </div>
            </div>

            <div className="stats-grid">
                <KpiCard tone="amber" label="Da convalidare" valore={bozzeAperte}
                    titolo="Bozze generate da GECOS in attesa di assegnatari e conferma"
                    icona={ico(<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>)} />
                <KpiCard tone="blue" label="Confermate" valore={conta("confermata")}
                    titolo="Pronte per la pubblicazione automatica delle 20:00"
                    icona={ico(<polyline points="20 6 9 17 4 12" />)} />
                <KpiCard tone="slate" label="Non generabili" valore={conta("non_generabile")}
                    titolo="Turni GECOS il cui percorso manca nell'anagrafica: va aggiunto in Anagrafiche"
                    icona={ico(<><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></>)} />
            </div>

            <ViewToggle viewMode={viewMode} onChange={onViewMode} />

            {viewMode === "table" ? (
                <div className={`table-container gp-inlav-table gp-bozze-table${fsBozze ? " gp-fullscreen-table" : ""}`}>
                    <RotateDevice active={fsBozze} maxWidth={1024} />
                    <div className="gp-table-topbar">
                        <div className="gp-table-title">{titoloBozze}</div>
                        <FullscreenBtn attivo={fsBozze} onClick={() => setFsBozze((v) => !v)} />
                    </div>
                    {statusBozze}
                    <div className="table-scroll">
                        <table>
                            <thead>
                                <tr>
                                    <th>Percorso</th>
                                    <th>Data giro</th>
                                    <th>Convalida entro</th>
                                    <th>Assegnatari</th>
                                    <th>Tratti</th>
                                    <th>Stato</th>
                                    <th className="col-actions">Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {gruppiPerData(bozze).map((g) => (
                                    <Fragment key={g.data}>
                                    <tr className="gp-bozze-gruppo"><td colSpan={7}>{etichettaGruppo(g)}</td></tr>
                                    {g.bozze.map((b) => (
                                    <tr key={b.id} className={b.stato === "bozza" ? "gp-bozza-aperta" : ""}>
                                        <td><span className="strong">{b.nome || "—"}</span><div className="codice" style={{ fontSize: 11 }}>{b.itinerario}{b.interacta_custom_id ? ` · ${b.interacta_custom_id}` : ""}</div></td>
                                        <td>{formatData(b.data)}</td>
                                        <td>{formatScadenza(b)}</td>
                                        <td>{(b.assegnatari_ids || []).length ? b.assegnatari_ids.map(nomeUtente).join(", ") : <span className="muted">—</span>}</td>
                                        <td><span className="muted">{b.nTratti} vie</span></td>
                                        <td>
                                            <span className={`badge ${bozzaStato(b.stato).cls}`} title={b.motivo || ""}>{bozzaStato(b.stato).label}</span>
                                            {b.motivo && <div className="gp-bozza-motivo">{b.motivo}</div>}
                                        </td>
                                        <td className="col-actions">{azioniBozza(b)}</td>
                                    </tr>
                                    ))}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                        {bozzeLoading && bozze.length === 0
                            ? <div className="gp-empty">Caricamento…</div>
                            : bozze.length === 0 && <div className="gp-empty">{VUOTO}</div>}
                    </div>
                </div>
            ) : (
                <>
                    <div className="gp-section-head">{titoloBozze}</div>
                    {statusBozze}
                    {bozzeLoading && bozze.length === 0 ? (
                        <div className="gp-empty">Caricamento…</div>
                    ) : bozze.length === 0 ? (
                        <div className="gp-empty">{VUOTO}</div>
                    ) : (
                        <div className="gp-cards-grid gp-inlav-cards">
                            {gruppiPerData(bozze).map((g) => (
                                <Fragment key={g.data}>
                                <div className="gp-bozze-gruppo-card">{etichettaGruppo(g)}</div>
                                {g.bozze.map((b) => (
                                <div className="gp-card" key={b.id}>
                                    <div className="gp-card-head">
                                        <div>
                                            <div className="gp-card-title">{b.nome || "—"}</div>
                                            <div className="codice">{b.itinerario}{b.interacta_custom_id ? ` · ${b.interacta_custom_id}` : ""}</div>
                                        </div>
                                        <span className={`badge ${bozzaStato(b.stato).cls}`}>{bozzaStato(b.stato).label}</span>
                                    </div>
                                    <div className="gp-card-body">
                                        <div className="gp-card-row"><span className="gp-card-label">Data giro</span><span className="gp-card-value">{formatData(b.data)}</span></div>
                                        <div className="gp-card-row"><span className="gp-card-label">Convalida entro</span><span className="gp-card-value">{formatScadenza(b)}</span></div>
                                        <div className="gp-card-row"><span className="gp-card-label">Assegnatari</span><span className="gp-card-value">{(b.assegnatari_ids || []).length ? b.assegnatari_ids.map(nomeUtente).join(", ") : "—"}</span></div>
                                        <div className="gp-card-row"><span className="gp-card-label">Tratti</span><span className="gp-card-value">{b.nTratti} vie</span></div>
                                        {b.motivo && <div className="gp-card-row"><span className="gp-card-label">Note</span><span className="gp-card-value">{b.motivo}</span></div>}
                                    </div>
                                    <div className="gp-card-actions">{azioniBozza(b)}</div>
                                </div>
                                ))}
                                </Fragment>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Convalida/modifica di una bozza. `top` quando la tabella bozze è in fullscreen
                (z 9999): l'overlay del modale deve stargli sopra (gp-overlay-top). */}
            {bozzaAperta && (
                <ModaleConvalidaBozza
                    bozza={bozzaAperta}
                    utentiOptions={utentiOptions}
                    utentiLoading={utentiLoading}
                    onClose={() => { setBozzaAperta(null); fetchBozze(); }}
                    onSaved={bozzaSalvata}
                    top={fsBozze}
                />
            )}
        </>
    );
};

export default SezionePianificati;
